import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Menu, X, Crown, User, LogOut, LayoutDashboard, Sun, Moon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '@/context/ThemeContext';
import { useLang } from '@/context/LanguageContext';
import { LanguageToggle } from '@/components/LanguageToggle';

const LINKS = [
  { to: '/', key: 'home' },
  { to: '/programs', key: 'programs' },
  { to: '/about', key: 'about' },
  { to: '/questionnaire', key: 'assessment' },
  { to: '/contact', key: 'contact' },
];

const ThemeButton = ({ testId }) => {
  const { theme, toggleTheme } = useTheme();
  const { t } = useLang();
  return (
    <button onClick={toggleTheme} className="w-9 h-9 rounded-full border border-[rgba(255,255,255,0.1)] flex items-center justify-center text-[#A3A19C] hover:text-[#D4AF37] hover:border-[rgba(212,175,55,0.3)] transition-colors duration-300" data-testid={testId} aria-label={t('common.toggle_theme')}>
      {theme === 'dark' ? <Sun className="w-4 h-4" strokeWidth={1.5} /> : <Moon className="w-4 h-4" strokeWidth={1.5} />}
    </button>
  );
};

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  const isActive = (path) => location.pathname === path;

  const handleLogout = () => { logout(); navigate('/'); };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass-header" data-testid="main-navbar">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center gap-1.5 sm:gap-2 group shrink-0" data-testid="nav-logo" dir="ltr">
            <Crown className="w-5 h-5 sm:w-6 sm:h-6 text-[#D4AF37] transition-transform duration-300 group-hover:rotate-12" strokeWidth={1.5} />
            <span className="brand-logo font-['Cormorant_Garamond'] text-[17px] sm:text-xl font-bold tracking-tight text-[#F5F2EB]">
              Princess<span className="text-[#D4AF37]">4</span>Fitness
            </span>
          </Link>

          <div className="hidden lg:flex items-center gap-9">
            {LINKS.map(link => (
              <Link key={link.to} to={link.to} data-testid={`nav-link-${link.key}`}
                className={`text-sm font-medium transition-colors duration-300 ${isActive(link.to) ? 'text-[#D4AF37]' : 'text-[#A3A19C] hover:text-[#F5F2EB]'}`}>
                {t(`nav.${link.key}`)}
              </Link>
            ))}
          </div>

          <div className="hidden lg:flex items-center gap-3">
            <LanguageToggle testId="lang-toggle" />
            <ThemeButton testId="theme-toggle" />
            {user ? (
              <div className="flex items-center gap-4">
                {user.role === 'admin' ? (
                  <Link to="/admin" className="btn-secondary text-xs py-2 px-5 inline-flex items-center gap-1.5" data-testid="nav-admin-btn">
                    <LayoutDashboard className="w-4 h-4" strokeWidth={1.5} /> {t('nav.admin')}
                  </Link>
                ) : (
                  <Link to="/dashboard" className="btn-secondary text-xs py-2 px-5 inline-flex items-center gap-1.5" data-testid="nav-dashboard-btn">
                    <User className="w-4 h-4" strokeWidth={1.5} /> {t('nav.dashboard')}
                  </Link>
                )}
                <button onClick={handleLogout} className="text-[#A3A19C] hover:text-[#F5F2EB] transition-colors duration-300 text-sm" data-testid="nav-logout-btn" aria-label={t('nav.signout')}>
                  <LogOut className="w-4 h-4 rtl:-scale-x-100" strokeWidth={1.5} />
                </button>
              </div>
            ) : (
              <>
                <Link to="/login" className="text-sm text-[#A3A19C] hover:text-[#F5F2EB] transition-colors duration-300 px-1" data-testid="nav-login-btn">
                  {t('nav.signin')}
                </Link>
                <Link to="/register?redirect=questionnaire" className="btn-primary text-xs py-2.5 px-6" data-testid="nav-cta-btn">
                  {t('nav.cta')}
                </Link>
              </>
            )}
          </div>

          <div className="lg:hidden flex items-center gap-1.5 sm:gap-2">
            <LanguageToggle testId="lang-toggle-mobile" />
            <ThemeButton testId="theme-toggle-mobile" />
            <button onClick={() => setOpen(!open)} className="text-[#F5F2EB] ms-1" data-testid="nav-mobile-toggle">
              {open ? <X className="w-6 h-6" strokeWidth={1.5} /> : <Menu className="w-6 h-6" strokeWidth={1.5} />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-[#121215] border-t border-[rgba(255,255,255,0.1)]">
            <div className="px-6 py-6 space-y-4">
              {LINKS.map(link => (
                <Link key={link.to} to={link.to} onClick={() => setOpen(false)} data-testid={`nav-mobile-${link.key}`}
                  className={`block text-base font-medium ${isActive(link.to) ? 'text-[#D4AF37]' : 'text-[#A3A19C]'}`}>
                  {t(`nav.${link.key}`)}
                </Link>
              ))}
              <div className="pt-4 border-t border-[rgba(255,255,255,0.1)] space-y-3">
                {user ? (
                  <>
                    <Link to={user.role === 'admin' ? '/admin' : '/dashboard'} onClick={() => setOpen(false)} className="block btn-secondary text-center text-sm py-2.5" data-testid="nav-mobile-dashboard">
                      {user.role === 'admin' ? t('nav.admin_panel') : t('nav.dashboard')}
                    </Link>
                    <button onClick={() => { handleLogout(); setOpen(false); }} className="block w-full text-center text-[#A3A19C] text-sm" data-testid="nav-mobile-logout">
                      {t('nav.signout')}
                    </button>
                  </>
                ) : (
                  <>
                    <Link to="/login" onClick={() => setOpen(false)} className="block text-center text-[#A3A19C] text-sm" data-testid="nav-mobile-login">{t('nav.signin')}</Link>
                    <Link to="/register?redirect=questionnaire" onClick={() => setOpen(false)} className="block btn-primary text-center text-sm py-2.5" data-testid="nav-mobile-cta">{t('nav.cta')}</Link>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
